import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Mic, MicOff, PhoneOff, Captions, Video as VideoIcon, VideoOff, BookmarkPlus, Loader2,
} from "lucide-react";

const Tile = ({ id, name, stream, showVideo, talking }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && stream) ref.current.srcObject = stream;
  }, [stream]);
  return (
    <div
      data-testid={`call-tile-${id}`}
      data-talking={talking ? "yes" : "no"}
      className={`relative aspect-video overflow-hidden rounded-2xl border bg-black transition-all ${
        talking ? "border-brand ring-2 ring-brand/60" : "border-white/10"
      }`}
    >
      <video
        ref={ref}
        autoPlay
        playsInline
        muted={id === "me"}
        className={`h-full w-full object-cover ${showVideo ? "" : "opacity-0"}`}
      />
      {!showVideo && (
        <span className="absolute inset-0 flex items-center justify-center font-display text-2xl font-bold text-brand">
          {(name || "?").slice(0, 1).toUpperCase()}
        </span>
      )}
      <span className="absolute bottom-2 start-2 rounded-full bg-black/60 px-2 py-0.5 text-xs">
        {name}
      </span>
    </div>
  );
};

const clock = (s) =>
  `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const QUALITY = {
  good: { color: "bg-forest", label: "Strong connection" },
  ok: { color: "bg-amber-400", label: "Connection is a little slow" },
  poor: { color: "bg-destructive", label: "Weak connection" },
};

export const CallPanel = ({ call, onClose, onSavePhrase }) => {
  const people = Object.entries(call.participants);
  const q = QUALITY[call.quality] || QUALITY.good;
  return (
    <div className="border-b border-white/10 bg-page p-4" data-testid="call-panel">
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-brand">
          <span className="h-2 w-2 animate-pulse rounded-full bg-brand" />
          Live call · {people.length} on the line
        </span>
        <span data-testid="call-timer" className="rounded-full bg-panel px-2.5 py-0.5 font-mono text-xs text-ink-soft">
          {clock(call.elapsed)}
        </span>
        <span
          data-testid="call-quality"
          data-quality={call.quality}
          title={q.label}
          className="inline-flex items-center gap-1.5 text-xs text-ink-soft"
        >
          <span className={`h-2 w-2 rounded-full ${q.color}`} />
          <span className="hidden sm:inline">{q.label}</span>
        </span>
        {call.reconnecting && (
          <span data-testid="call-reconnecting" className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 px-2.5 py-0.5 text-xs text-amber-300">
            <Loader2 className="h-3 w-3 animate-spin" /> Reconnecting…
          </span>
        )}
        <div className="ms-auto flex flex-wrap gap-2">
          <Button
            data-testid="call-mute-button" variant="outline" size="sm"
            className="min-h-[36px] rounded-full active:scale-95" onClick={call.toggleMute}
          >
            {call.muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            <span className="ms-1.5 hidden sm:inline">{call.muted ? "Unmute" : "Mute"}</span>
          </Button>
          <Button
            data-testid="call-camera-button" variant="outline" size="sm"
            className="min-h-[36px] rounded-full active:scale-95" onClick={call.toggleCamera}
          >
            {call.cameraOn ? <VideoIcon className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
            <span className="ms-1.5 hidden sm:inline">
              {call.cameraOn ? "Camera on" : "Camera off"}
            </span>
          </Button>
          <Button
            data-testid="call-captions-button" variant="outline" size="sm"
            className="min-h-[36px] rounded-full active:scale-95" onClick={call.toggleCaptions}
          >
            <Captions className="h-4 w-4" />
            <span className="ms-1.5 hidden sm:inline">
              {call.captionsOn ? "Captions on" : "Captions off"}
            </span>
          </Button>
          <Button
            data-testid="call-leave-button" size="sm"
            className="min-h-[36px] rounded-full bg-destructive px-4 hover:bg-destructive/90 active:scale-95"
            onClick={() => {
              call.leave();
              onClose?.();
            }}
          >
            <PhoneOff className="h-4 w-4" />
            <span className="ms-1.5 hidden sm:inline">Leave</span>
          </Button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {people.map(([id, p]) => (
          <Tile
            key={id} id={id} name={p.name} stream={p.stream}
            showVideo={call.video && (id !== "me" || call.cameraOn)}
            talking={!!call.speaking?.[id]}
          />
        ))}
        {people.length === 1 && (
          <div className="flex aspect-video items-center justify-center rounded-2xl border border-dashed border-white/15 p-4 text-center text-xs text-ink-soft">
            Ringing… waiting for someone in this room to pick up.
          </div>
        )}
      </div>

      <div className="mt-4 min-h-[64px] rounded-2xl border border-white/10 bg-panel p-3" data-testid="call-captions">
        {call.captions.length === 0 ? (
          <p className="text-xs text-ink-soft">
            <VideoIcon className="me-1.5 inline h-3.5 w-3.5" />
            Translated captions appear here a few seconds after each sentence.
          </p>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {call.captions.map((c, i) => (
              <li key={c.id} dir="auto" className="flex items-start gap-2">
                <span className="flex-1">
                  <span className="text-xs font-semibold text-brand">{c.from_name}: </span>
                  {c.text}
                </span>
                {onSavePhrase && c.text && (
                  <button
                    data-testid={`save-caption-${i}`}
                    onClick={() => onSavePhrase(c.text)}
                    title="Save to my phrasebook"
                    className="shrink-0 opacity-60 transition-opacity hover:opacity-100"
                  >
                    <BookmarkPlus className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
